import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

type SpinnerVariant = "border" | "icon";

interface LoadingSpinnerProps {
  /** Visual variant: "border" for CSS-only spinner, "icon" for Loader2 icon */
  variant?: SpinnerVariant;
  /** Size class for the spinner (e.g. "h-6 w-6") */
  size?: string;
  /** Optional label shown below the spinner */
  label?: string;
  /** Additional wrapper classes */
  className?: string;
}

/**
 * Shared loading spinner used across lazy-loaded routes and data-loading states.
 *
 * Two variants:
 * - `border` (default): lightweight CSS-only animated ring
 * - `icon`: lucide Loader2 icon with spin animation
 */
export function LoadingSpinner({
  variant = "border",
  size,
  label,
  className,
}: LoadingSpinnerProps) {
  const sizeClass = size ?? (variant === "border" ? "w-6 h-6" : "h-6 w-6");

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3",
        className,
      )}
      role="status"
      aria-label={label ?? "Loading"}
    >
      {variant === "border" ? (
        <div
          className={cn(
            "border-2 border-primary border-t-transparent rounded-full animate-spin",
            sizeClass,
          )}
        />
      ) : (
        <Loader2 className={cn("animate-spin text-primary", sizeClass)} />
      )}
      {label && (
        <span className="text-sm text-muted-foreground">{label}</span>
      )}
    </div>
  );
}
